// DAL Phase 2 — Étape Infos du wizard (3 blocs : Bénéficiaire / Logement /
// Situation handicap). SOURCE UNIQUE BIDIRECTIONNELLE : chaque champ lit ET écrit
// la source canonique (metier_clients pour le bénéficiaire, dossier.logement pour
// le logement) via le PATCH per-field /save-mobile (le backend route vers la
// source canonique, ZÉRO copie infos_dal — cf. dal_routes/dal_source_unique).
// Handicap + zone du logement = spécifiques DAL (restent dans infos_dal).
//
// Write-through AVEC CONFIRMATION (décision JJ #1) : si l'utilisateur modifie un
// champ DÉJÀ renseigné, une confirmation est demandée avant d'écraser ; un champ
// vide est écrit directement.
//
// ⚠️ Les sous-composants de champ (FieldTxt/FieldSel/Chip/Field) sont définis au
// niveau MODULE (hors du composant) — sinon ils seraient recréés à chaque render
// et l'input perdrait le focus / l'onBlur ne tirerait pas (cf. piège MoneyInput).
import { useState, useRef } from "react";
import { Users, MapPin, Accessibility } from "lucide-react";
import { useConfigList } from "../../hooks/useConfigList";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";

const TYPE_LOG_OPTS = [["", "—"], ["maison", "Maison"], ["appartement", "Appartement"]];
const STATUT_OPTS = [
  ["", "—"], ["proprietaire_occupant", "Propriétaire occupant"],
  ["proprietaire_bailleur", "Propriétaire bailleur"], ["locataire", "Locataire"],
  ["heberge", "Hébergé(e)"],
];
const ZONE_OPTS = [["urbaine", "Urbaine"], ["rurale", "Rurale"], ["periurbaine", "Périurbaine"]];
const OUINON = [["", "—"], ["oui", "Oui"], ["non", "Non"]];
const RECONN_OPTS = [["non_evalue", "Non évaluée"], ["oui", "Oui"], ["non", "Non"]];
const REGIME_OPTS = [
  ["", "—"], ["general", "Régime général"], ["agricole", "Régime agricole (MSA)"],
  ["autonome", "Indépendant / autonome"], ["fonction_publique", "Fonction publique"],
  ["autre", "Autre"],
];
const CAS_OPTS = [["", "—"], ["cas_1", "Oui — Cas n°1"], ["cas_2", "Oui — Cas n°2"], ["non", "Non"]];

// Coercition des champs numériques avant écriture canonique (Client / dossier.logement
// attendent des nombres pour ces champs).
const NUM_INT = new Set(["nb_personnes_foyer", "nb_pieces", "nb_niveaux", "annee_construction"]);
const NUM_FLOAT = new Set(["surface_habitable"]);
function coerce(key, v) {
  if (v === "" || v == null) return v;
  if (NUM_INT.has(key)) { const n = parseInt(v, 10); return Number.isNaN(n) ? v : n; }
  if (NUM_FLOAT.has(key)) { const n = parseFloat(v); return Number.isNaN(n) ? v : n; }
  return v;
}
const isEmpty = (v) => v === "" || v === null || v === undefined;
const lbl = (opts, val) => (opts.find(o => String(o[0]) === String(val ?? "")) || [val, val])[1];

// ─── Styles charte OmniHub (dual-mode via tokens var(--oh-*)) ────────────────
const successStyle = { background: "var(--oh-success-bg)", color: "var(--oh-success-text)", borderColor: "var(--oh-success-border)" };
const cardStyle = { background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" };
const inputStyle = { background: "var(--oh-bg-elevated)", color: "var(--oh-text)", borderColor: "var(--oh-border)" };
const accentTint = { background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)" };
const chipStyle = { background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)", borderColor: "var(--oh-accent)" };
const btnPrimary = { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" };
const btnDiscreet = { background: "transparent", border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)" };

function ageFromDOB(dob) {
  if (!dob) return null;
  const m = String(dob).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const b = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00`);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) a--;
  return a >= 0 && a < 130 ? a : null;
}

// ─── Sous-composants champ (niveau MODULE — stables entre renders) ───────────
// Chips source (client / dossier / DAL / calculé) : un seul accent charte (pas de
// palette décorative — la sémantique de source reste portée par le libellé).
const Chip = ({ kind, children }) => (
  <span className="text-[9px] px-1.5 py-px rounded-full border font-medium" style={chipStyle}>{children}</span>
);
const Field = ({ label, chip, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-xs flex items-center gap-1.5" style={{ color: "var(--oh-text-secondary)" }}>{label} {chip}</Label>
    {children}
  </div>
);
const FieldTxt = ({ path, vals, onSet, onCommit, type = "text", placeholder }) => (
  <Input type={type} value={vals[path] ?? ""} placeholder={placeholder}
    onChange={e => onSet(path, e.target.value)}
    onBlur={e => onCommit(path, e.target.value)}
    style={inputStyle} data-testid={`infos-${path}`} />
);
const FieldSel = ({ path, vals, onSet, onCommit, opts }) => (
  <select value={vals[path] ?? ""} data-testid={`infos-${path}`}
    onChange={e => { onSet(path, e.target.value); onCommit(path, e.target.value, opts); }}
    className="w-full h-10 px-3 rounded-md border text-sm" style={inputStyle}>
    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

// `section` (nouveau, DAL Phase 3) : rend UN SEUL bloc quand fourni
// ("beneficiaire" | "logement" | "handicap") — utilisé par le menu latéral
// desktop (embedded). Absent/null → rend les 3 blocs (comportement mobile
// historique inchangé).
export default function DalInfosStep({ initialData = {}, patchField, clientName = "Bénéficiaire", section = null }) {
  const canon = initialData?.canonical || {};
  const cat = initialData?.categorie_ressources || null;
  const hdcp0 = initialData?.handicap || {};
  const zone0 = initialData?.logement?.zone_logement || "urbaine";

  const init = {
    "beneficiaire.date_naissance": canon.date_naissance ?? "",
    "beneficiaire.nb_personnes_foyer": canon.nb_personnes_foyer ?? "",
    "logement.type_logement": canon.type_logement ?? "",
    "logement.statut_occupation": canon.statut_occupation ?? "",
    "logement.annee_construction": canon.annee_construction ?? "",
    "logement.surface_habitable": canon.surface_habitable ?? "",
    "logement.nb_pieces": canon.nb_pieces ?? "",
    "logement.nb_niveaux": canon.nb_niveaux ?? "",
    "logement.zone_logement": zone0,
    "handicap.reconnaissance_handicap": hdcp0.reconnaissance_handicap ?? "non_evalue",
    "handicap.regime_retraite": hdcp0.regime_retraite ?? "",
    "handicap.equipement_deplacement": hdcp0.equipement_deplacement ?? "",
    "handicap.equipements_detail": hdcp0.equipements_detail ?? "",
    "handicap.evaluation_ergotherapeute": hdcp0.evaluation_ergotherapeute ?? "",
    "handicap.condition_cas": hdcp0.condition_cas ?? "",
  };
  const [vals, setVals] = useState(init);
  const orig = useRef({ ...init });
  const [confirm, setConfirm] = useState(null);

  // Selects data-driven : options issues de metier_config_listes (comme DALForm/O1).
  // Repli sur les constantes hard-codées si la liste n'est pas (encore) chargée /
  // seedée → zéro régression. Un "—" vide est préfixé pour permettre le non-renseigné.
  const { items: cfgTypeLog } = useConfigList("types_logement");
  const { items: cfgStatut } = useConfigList("statuts_occupation");
  const cfgOpts = (items, fallback) =>
    (Array.isArray(items) && items.length)
      ? [["", "—"], ...items.map((i) => [i.id, i.label])]
      : fallback;
  const typeLogOpts = cfgOpts(cfgTypeLog, TYPE_LOG_OPTS);
  const statutOpts = cfgOpts(cfgStatut, STATUT_OPTS);

  const onSet = (path, v) => setVals(p => ({ ...p, [path]: v }));

  const persist = (path, v) => {
    const key = path.split(".")[1];
    patchField(path, coerce(key, v));
    orig.current[path] = v;
  };

  // Commit avec confirmation si le champ était déjà renseigné et change.
  const onCommit = (path, v, opts) => {
    const o = orig.current[path];
    if (String(o ?? "") === String(v ?? "")) return;          // inchangé
    if (isEmpty(o)) { persist(path, v); return; }              // vide → écriture directe
    setConfirm({                                               // déjà rempli + différent → confirmation
      path, newVal: v,
      oldLabel: opts ? lbl(opts, o) : o,
      newLabel: opts ? lbl(opts, v) : v,
    });
  };

  const cancelConfirm = () => { if (confirm) onSet(confirm.path, orig.current[confirm.path]); setConfirm(null); };

  const dobAge = ageFromDOB(vals["beneficiaire.date_naissance"]);
  const txt = (path, extra = {}) => <FieldTxt path={path} vals={vals} onSet={onSet} onCommit={onCommit} {...extra} />;
  const sel = (path, opts) => <FieldSel path={path} vals={vals} onSet={onSet} onCommit={onCommit} opts={opts} />;

  // Filtre par section (menu latéral desktop). Sans `section` → les 3 blocs.
  const showBenef = !section || section === "beneficiaire";
  const showLog = !section || section === "logement";
  const showHdcp = !section || section === "handicap";
  const showHeader = !section;

  return (
    <div className="space-y-4" data-testid="dal-infos-step">
      {showHeader && (
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={accentTint}><Users className="w-5 h-5" /></div>
        <div><h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>Infos dossier</h2><p className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>Saisie ici = mise à jour de la fiche (source unique)</p></div>
      </div>
      )}

      {/* BLOC 1 — Bénéficiaire */}
      {showBenef && (
      <div className="rounded-xl border overflow-hidden" style={cardStyle}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b text-sm font-medium" style={{ borderColor: "var(--oh-border)", color: "var(--oh-text)" }}><Users className="w-4 h-4" style={{ color: "var(--oh-accent-text)" }} /> Bénéficiaire — {clientName}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
          <Field label="Date de naissance" chip={<Chip kind="cli">client</Chip>}>{txt("beneficiaire.date_naissance", { type: "date" })}</Field>
          <Field label="Âge (auto)"><div className="h-10 px-3 rounded-md border text-sm flex items-center" style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)" }} data-testid="infos-age-auto">{dobAge != null ? `${dobAge} ans` : "—"}</div></Field>
          <Field label="Nombre d'occupants" chip={<Chip kind="cli">client</Chip>}>{txt("beneficiaire.nb_personnes_foyer", { type: "number", placeholder: "1" })}</Field>
          <div className="sm:col-span-2">
            <Field label="Taux de prise en charge MPA" chip={<Chip kind="dal">calculé</Chip>}>
              <div data-testid="infos-taux">
                {cat?.taux_pct != null
                  ? <span className="inline-block text-sm px-3 py-1.5 rounded-md border font-medium" style={successStyle}>{cat.label || `${cat.taux_pct} %`}</span>
                  : <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{cat?.label || "Calcul indisponible (RFR / foyer manquant)"}</span>}
              </div>
            </Field>
          </div>
        </div>
      </div>
      )}

      {/* BLOC 2 — Logement */}
      {showLog && (
      <div className="rounded-xl border overflow-hidden" style={cardStyle}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b text-sm font-medium" style={{ borderColor: "var(--oh-border)", color: "var(--oh-text)" }}><MapPin className="w-4 h-4" style={{ color: "var(--oh-accent-text)" }} /> Logement</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
          <Field label="Type d'habitation" chip={<Chip kind="log">dossier</Chip>}>{sel("logement.type_logement", typeLogOpts)}</Field>
          <Field label="Statut d'occupation" chip={<Chip kind="log">dossier</Chip>}>{sel("logement.statut_occupation", statutOpts)}</Field>
          <Field label="Année de construction" chip={<Chip kind="log">dossier</Chip>}>{txt("logement.annee_construction", { type: "number", placeholder: "1970" })}</Field>
          <Field label="Surface habitable (m²)" chip={<Chip kind="log">dossier</Chip>}>{txt("logement.surface_habitable", { type: "number", placeholder: "80" })}</Field>
          <Field label="Nombre de pièces" chip={<Chip kind="log">dossier</Chip>}>{txt("logement.nb_pieces", { type: "number", placeholder: "4" })}</Field>
          <Field label="Nombre de niveaux" chip={<Chip kind="log">dossier</Chip>}>{txt("logement.nb_niveaux", { type: "number", placeholder: "1" })}</Field>
          <Field label="Zone du logement" chip={<Chip kind="dal">DAL</Chip>}>{sel("logement.zone_logement", ZONE_OPTS)}</Field>
        </div>
      </div>
      )}

      {/* BLOC 3 — Situation handicap */}
      {showHdcp && (
      <div className="rounded-xl border overflow-hidden" style={cardStyle}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b text-sm font-medium" style={{ borderColor: "var(--oh-border)", color: "var(--oh-text)" }}><Accessibility className="w-4 h-4" style={{ color: "var(--oh-accent-text)" }} /> Situation handicap</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
          <Field label="Reconnaissance administrative" chip={<Chip kind="dal">DAL</Chip>}>{sel("handicap.reconnaissance_handicap", RECONN_OPTS)}</Field>
          <Field label="Régime de retraite" chip={<Chip kind="dal">DAL</Chip>}>{sel("handicap.regime_retraite", REGIME_OPTS)}</Field>
          <Field label="Équipement de déplacement ?" chip={<Chip kind="dal">DAL</Chip>}>{sel("handicap.equipement_deplacement", OUINON)}</Field>
          <Field label="Si oui, lequel ?" chip={<Chip kind="dal">DAL</Chip>}>{txt("handicap.equipements_detail", { placeholder: "Déambulateur, fauteuil…" })}</Field>
          <Field label="Évaluation ergothérapeute < 6 mois" chip={<Chip kind="dal">DAL</Chip>}>{sel("handicap.evaluation_ergotherapeute", OUINON)}</Field>
          <Field label="Satisfait conditions (Cas 1/2)" chip={<Chip kind="dal">DAL</Chip>}>{sel("handicap.condition_cas", CAS_OPTS)}</Field>
        </div>
      </div>
      )}

      {/* Confirmation d'écrasement (write-through avec confirmation) */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) cancelConfirm(); }}>
        <DialogContent className="sm:max-w-md" style={cardStyle} data-testid="infos-confirm-overwrite">
          <DialogHeader>
            <DialogTitle style={{ color: "var(--oh-text)" }}>Remplacer une valeur existante ?</DialogTitle>
            <DialogDescription style={{ color: "var(--oh-text-secondary)" }}>
              Cette valeur existe déjà : <b style={{ color: "var(--oh-text)" }}>{String(confirm?.oldLabel ?? "")}</b>.<br />
              La remplacer par <b style={{ color: "var(--oh-text)" }}>{String(confirm?.newLabel ?? "")}</b> ? La fiche dossier sera mise à jour.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" style={btnDiscreet} data-testid="infos-confirm-cancel" onClick={cancelConfirm}>Annuler</Button>
            <Button style={btnPrimary} data-testid="infos-confirm-ok"
              onClick={() => { if (confirm) persist(confirm.path, confirm.newVal); setConfirm(null); }}>
              Remplacer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
